import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import classNames from 'classnames'
import { Icon } from '@pandiafi/design-system'

import { useContractPaths } from '../../../../hooks/useContractPaths/useContractPaths'
import { ComingSoonLink } from '../../components/ComingSoonLink'
import { FeatureFlag } from '../../../../../common/validations'
import { useModuleConfig } from '../../../../hooks/useModuleConfig/useModuleConfig'
import { Permissions } from '../../../Permissions'

export type NavbarLink = ReactElement

export type NavbarLinks = {
  homeLinks: NavbarLink[]
  transactionsLinks: NavbarLink[]
  userInfoLinks: NavbarLink[]
  propertyLinks: NavbarLink[]
}

export const useNavbarLinks = (featureFlags?: FeatureFlag): NavbarLinks => {
  const { t } = useTranslation(['common'])
  const {
    frontPagePath,
    defectNoticePath,
    terminateContractPath,
    messagesPath,
    userInfoPath,
    paymentHistoryPath,
    documentsPath,
    buildingPath,
    bulletinBoardPath,
    contactInfoPath,
    parkingPath,
    storagesPath,
    saunaReservationsPath,
    gymBookingPath,
    laundryReservationsPath,
    commonAreaReservationsPath,
    gymReservationsPath,
  } = useContractPaths()

  const modules = useModuleConfig()

  const linkClass = (isActive: boolean): string =>
    classNames('hover:underline hover:text-brand-7', {
      'text-brand-6': !isActive,
      'underline font-medium text-brand-7': isActive,
    })

  const messagingModule = modules.messaging ?? false

  // defect notice: enabled when not termination-only and not explicitly false
  const defectNoticeEnabled =
    !modules.contractTerminationOnly && modules.defectNotice.enabled !== false

  const externalDefectUrl = defectNoticeEnabled
    ? modules.defectNotice.customDefectNoticeLinkUrl
    : undefined

  const contractModification = modules.contractModification ?? false

  // === contractTerminationOnly: minimal nav ===
  if (modules.contractTerminationOnly) {
    const homeLinks: NavbarLink[] = [
      <NavLink
        key="home"
        to={frontPagePath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.home')}
      </NavLink>,
    ]

    const transactionsLinks: NavbarLink[] = [
      <Permissions key="terminate" feature="contractChanges">
        <NavLink
          to={terminateContractPath}
          className={({ isActive }) => linkClass(isActive)}
          end
        >
          {contractModification
            ? t('common:navbar.modifyContract')
            : t('common:navbar.terminateContract')}
        </NavLink>
      </Permissions>,
    ]

    const userInfoLinks: NavbarLink[] = [
      <NavLink
        key="userinfo"
        to={userInfoPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.userInfo')}
      </NavLink>,
    ]

    const propertyLinks: NavbarLink[] = []

    return { homeLinks, transactionsLinks, userInfoLinks, propertyLinks }
  }

  // === FULL build ===
  const homeLinks: NavbarLink[] = [
    <NavLink
      key="home"
      to={frontPagePath}
      className={({ isActive }) => linkClass(isActive)}
      end
    >
      {t('common:navbar.home')}
    </NavLink>,
  ]

  const transactionsLinks: NavbarLink[] = []

  if (messagingModule) {
    transactionsLinks.push(
      <NavLink
        key="messages"
        to={messagesPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.messages')}
      </NavLink>
    )
  }

  // Defect notice: either internal OR external (mutually exclusive)
  if (defectNoticeEnabled && !externalDefectUrl) {
    transactionsLinks.push(
      <NavLink
        key="defect-internal"
        to={defectNoticePath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.faultReport')}
      </NavLink>
    )
  } else if (defectNoticeEnabled && externalDefectUrl) {
    transactionsLinks.push(
      <a
        key="defect-external"
        href={externalDefectUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass(false)}
      >
        {t('common:navbar.faultReport')} <Icon icon="external-link" />
      </a>
    )
  }

  // Terminate contract
  transactionsLinks.push(
    <Permissions key="terminate" feature="contractChanges">
      <NavLink
        to={terminateContractPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {contractModification
          ? t('common:navbar.modifyContract')
          : t('common:navbar.terminateContract')}
      </NavLink>
    </Permissions>
  )

  if (modules.parkingSpots.enabled) {
    transactionsLinks.push(
      <NavLink
        key="parkingSpots"
        to={parkingPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.parkingSpots')}
      </NavLink>
    )
  }
  if (modules.storages?.enabled) {
    transactionsLinks.push(
      <NavLink
        key="storages"
        to={storagesPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.storages')}
      </NavLink>
    )
  }

  if (modules.sauna?.enabled) {
    transactionsLinks.push(
      <NavLink
        key="saunaReservations"
        to={saunaReservationsPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.saunaReservations')}
      </NavLink>
    )
  }

  if (modules.laundryReservation.enabled) {
    transactionsLinks.push(
      <NavLink
        key="laundryReservations"
        to={laundryReservationsPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.laundryReservations', 'Laundry')}
      </NavLink>
    )
  }

  if (modules.gym.enabled) {
    transactionsLinks.push(
      <NavLink
        key="gymBooking"
        to={gymBookingPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.gymBooking', 'Gym')}
      </NavLink>
    )
  }

  if (modules.gymReservations.enabled) {
    transactionsLinks.push(
      <NavLink
        key="gymReservations"
        to={gymReservationsPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.gymReservations')}
      </NavLink>
    )
  }

  if (modules.commonAreaReservations.enabled) {
    transactionsLinks.push(
      <NavLink
        key="commonAreaReservations"
        to={commonAreaReservationsPath}
        className={({ isActive }) => linkClass(isActive)}
        end
      >
        {t('common:navbar.commonAreaReservations')}
      </NavLink>
    )
  }

  const userInfoLinks: NavbarLink[] = [
    <NavLink
      key="userinfo"
      to={userInfoPath}
      className={({ isActive }) => linkClass(isActive)}
      end
    >
      {t('common:navbar.userInfo')}
    </NavLink>,
  ]

  if (featureFlags?.paymentInfo) {
    userInfoLinks.push(
      <Permissions key="payments" feature="payments">
        <NavLink
          to={paymentHistoryPath}
          className={({ isActive }) => linkClass(isActive)}
          end
        >
          {t('common:navbar.payments')}
        </NavLink>
      </Permissions>
    )
  } else {
    userInfoLinks.push(
      <Permissions key="payments-soon" feature="payments">
        <ComingSoonLink>{t('common:navbar.payments')}</ComingSoonLink>
      </Permissions>
    )
  }

  const propertyLinks: NavbarLink[] = [
    <NavLink
      key="documents"
      to={documentsPath}
      className={({ isActive }) => linkClass(isActive)}
      end
    >
      {t('common:navbar.documents')}
    </NavLink>,
    <NavLink
      key="building"
      to={buildingPath}
      className={({ isActive }) => linkClass(isActive)}
      end
    >
      {t('common:navbar.building')}
    </NavLink>,
    <NavLink
      key="contact"
      to={contactInfoPath}
      className={({ isActive }) => linkClass(isActive)}
      end
    >
      {t('common:navbar.contactInfo')}
    </NavLink>,
    <NavLink
      key="bulletin"
      to={bulletinBoardPath}
      className={({ isActive }) => linkClass(isActive)}
      end
    >
      {t('common:navbar.bulletinBoard')}
    </NavLink>,
  ]

  return { homeLinks, transactionsLinks, userInfoLinks, propertyLinks }
}
